import React, { useEffect, useState } from 'react';
import { Loader, Notification } from 'is24-corecss';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCustomerVerificationStatus } from '../../api/tvs/customer/customerVerificationStatus';
import { DSA_DOCUMENTS_PENDING_PATH, DSA_DOCUMENT_CHECK_FAILED_PATH } from '../../routePaths';
import { resolveInReviewVerificationRoutePath } from '../verificationLandingPage/verificationRouting';
import { routeWithSearch } from '../../utils/routeWithSearch';

const STATUS_POLL_INTERVAL_MS = 5000;

const DsaPendingPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [showStatusError, setShowStatusError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: number | undefined;

    const checkVerificationStatus = async () => {
      try {
        const verificationStatus = await getCustomerVerificationStatus();
        const routePath = resolveInReviewVerificationRoutePath(verificationStatus);

        if (isMounted) {
          setShowStatusError(false);
        }
        if (isMounted && routePath !== DSA_DOCUMENTS_PENDING_PATH) {
          navigate(routeWithSearch(routePath, location.search), {
            replace: true,
            ...(routePath === DSA_DOCUMENT_CHECK_FAILED_PATH && verificationStatus.rejectionReason
              ? {
                  state: {
                    rejectionReason: verificationStatus.rejectionReason,
                    ...(verificationStatus.nameMismatch ? { nameMismatch: verificationStatus.nameMismatch } : {}),
                  },
                }
              : {}),
          });
        }
      } catch {
        if (isMounted) {
          setShowStatusError(true);
        }
      } finally {
        if (isMounted) {
          timeoutId = window.setTimeout(checkVerificationStatus, STATUS_POLL_INTERVAL_MS);
        }
      }
    };

    void checkVerificationStatus();

    return () => {
      isMounted = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [location.search, navigate]);

  return (
    <div className='grid grid-flex grid-justify-center'>
      <div className='grid-item one-whole'>
        {showStatusError && (
          <Notification
            className='margin-bottom-l'
            title={t('DsaPendingPage.errorTitle')}
            message={t('DsaPendingPage.errorDescription')}
            type='error'
            showCloseIcon={false}
          />
        )}
        <div className='font-heading-small-bold margin-top-xl'>{t('DsaPendingPage.title')}</div>
        <div className='margin-top-xl'>{t('DsaPendingPage.description')}</div>
        <div className='font-s margin-top-s'>{t('DsaPendingPage.pollingHint')}</div>
        <div className='one-whole grid-align-center' style={{ justifyItems: 'center' }}>
          <Loader size='medium' aria-label={t('DsaPendingPage.loadingLabel')} className='margin-vertical-xxl' />
        </div>
      </div>
    </div>
  );
};

export default DsaPendingPage;
